<script setup>
const props = defineProps({
  singleFileOnly: {
    type: Boolean,
    required: false,
    default: false
  },
  accept: {
    type: String,
    required: false,
    default: "*"
  }
});

const emit = defineEmits(["filesChanged"]);

const { t } = useI18n();

const isDragging = ref(false);
const files = ref([]);
const fileInputRef = ref(null);

watch(
  () => files,
  () => {
    if (props.singleFileOnly && files.value.length > 1) {
      files.value = [files.value[files.value.length - 1]];
    }

    emit("filesChanged", files.value);
  }, {
    deep: true
  });

const onChange = () => {
  const incomingFiles = [...fileInputRef.value.files];
  addFiles(incomingFiles);
};

const addFiles = (incomingFiles) => {
  const uniqueFiles = incomingFiles.filter(checkFileDoesNotExistYet);
  files.value.push(...uniqueFiles);
};

const generateThumbnail = (file) => {
  const fileSrc = URL.createObjectURL(file);
  setTimeout(() => {
    URL.revokeObjectURL(fileSrc);
  }, 1000);
  return fileSrc;
};

const makeName = (name) => (
  name.split(".")[0].substring(0, 3) +
    "..." +
    name.split(".")[name.split(".").length - 1]
);

const remove = (i) => {
  files.value.splice(i, 1);
};

const dragOver = (e) => {
  e.preventDefault();
  isDragging.value = true;
};

const dragLeave = () => {
  isDragging.value = false;
};

const drop = (e) => {
  e.preventDefault();
  const incomingFiles = [...e.dataTransfer.files];
  addFiles(incomingFiles);
  isDragging.value = false;
};

const checkFileDoesNotExistYet = (file) =>
  !files.value.map((existingFile) => checkFileEquality(existingFile, file))
    .reduce((acc, cur) => acc || cur, false);
const checkFileEquality = (fileA, fileB) => fileA.name === fileB.name && fileA.size === fileB.size && fileA.type === fileB.type && fileA.lastModified === fileB.lastModified;

const letGoText = computed(() => {
  if (props.singleFileOnly) {
    return t("file_upload.drop_single_file");
  } else {
    return t("file_upload.drop_multiple_files");
  }
});

const dropHereOrClickText = computed(() => {
  if (props.singleFileOnly) {
    // TODO: add bold (lost during merge)
    // "Datei hier ablegen oder <u class='click'>klicken</u> zum hochladen.";
    return t("file_upload.upload_single_file");
  } else {
    // "Dateien hier ablegen oder <u class='click'>klicken</u> zum hochladen.";
    return t("file_upload.upload_multiple_files");
  }
});
</script>

<template>
  <div class="dropzone-container">
    <div
      class="file-drop-grid"
      @dragover="dragOver"
      @dragleave="dragLeave"
      @drop="drop"
    >
      <input
        id="fileInput"
        ref="fileInputRef"
        type="file"
        multiple
        name="file"
        class="hidden-input"
        :accept="props.accept"
        @change="onChange"
      >

      <div class="file-label">
        <label v-if="isDragging" for="fileInput">
          {{ letGoText }}
        </label>

        <label v-else for="fileInput" v-html="dropHereOrClickText" />
      </div>

      <div v-if="files.length && !props.singleFileOnly" class="preview-container row">
        <div v-for="file in files" :key="file.name" class="column">
          <div class="image-container">
            <img class="preview-img" :src="generateThumbnail(file)">
            <button
              type="button"
              title="Remove file"
              class="btn btn-secondary btn-trash-round"
              @click="remove(files.indexOf(file))"
            >
              <CareDayIcon name="trash" style="width: 80%; height: 80%;" />
            </button>
            <p :title="file.name">
              {{ makeName(file.name) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-container {
  position: relative;
}

.btn-trash-round {
  position: absolute;
  right: 0;
}

.dropzone-container {
  width: 100%;
  display: grid;
  background: #f7fafc;
  cursor: pointer;
}

.file-drop-grid{
  display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 25% 75%;
    grid-template-areas:
          "label"
          "preview";
}

.hidden-input {
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
  display: grid;
}

.file-label label {
  width: 100%;
  height: 100%;
  display: grid;
  grid-area: label;
  font-size: 1.5rem;
  display: block;
  cursor: pointer;
  text-align: center;
  padding: 0.8rem;
}

.preview-container {
  width: 100%;
  height: 100%;
  display: grid;
  grid-area: preview;
  max-height:100%;
  overflow: auto;
}

.preview-img {
  width: 100%;
  height: 100%;
  background-color:white;
}

.btn-trash-round{
  width: 3rem;
  height: 3rem;
  float: right !important;
  border-radius: 0 0.2rem 0 0.6rem !important;
  z-index: 3;
}

p {
  text-align: center;
}

.row {
  display: flex;
  flex-wrap: wrap;
  padding: 0 4px;
}

/* Create four equal columns that sits next to each other */
.column {
  flex: 25%;
  max-width: 25%;
  padding: 0 4px;
}

.column img {
  vertical-align: middle;
  width: 100%;
}

.click {
  cursor: pointer;
}

/* Responsive layout - makes a two column-layout instead of four columns */
@media screen and (max-width: 800px) {
  .column {
    flex: 50%;
    max-width: 50%;
  }
}

/* Responsive layout - makes the two columns stack on top of each other instead of next to each other */
@media screen and (max-width: 600px) {
  .column {
    flex: 100%;
    max-width: 100%;
  }
}
</style>
